import { restClient } from '@/api/client'
import { endpoints } from '@/api/endpoints'

const q = (p: Record<string, unknown>) => new URLSearchParams(
  Object.entries(p)
    .filter(([, v]) => v !== undefined && v !== null && v !== '')
    .reduce((a, [k, v]) => ({ ...a, [k]: String(v) }), {}),
).toString()

// ── Response shapes (from the live API) ──────────────────────────────────────
export interface GpsPerson {
  id: number
  name: string
  lat: number
  lng: number
  speed: number
}

/** Row of GET admin/gps/active-sessions */
export interface ActiveSession {
  tracking_session_id: number
  booking_session_id: number
  booking_id: number
  trainer: GpsPerson | null
  student: GpsPerson | null
  phase: string
  started_at: string
}

/** Row of GET admin/gps/search — leaner, ids only */
export interface SearchSession {
  tracking_session_id: number
  booking_session_id: number
  booking_id: number
  trainer_id: number
  student_id: number | null
  phase: string
  status: string
  started_at: string
}

/** GET admin/gps/session/{id} */
export interface SessionDetail {
  tracking_session_id: number
  booking_session_id: number
  booking_id: number
  trainer_id: number
  trainer_name: string
  student_id: number | null
  student_name: string | null
  phase: string
  status: string
  started_at: string
  ended_at: string | null
  duration_sec: number
  is_active: boolean
}

/** Point of GET admin/gps/session/{trackingId}/route */
export interface RoutePoint {
  lat: number
  lng: number
  speed: number
  bearing: number
  recorded_at: string
}

interface Paged<T> { page: number; sessions: T[]; total: number }

export const gpsService = {
  activeSessions: (p: { page?: number; limit?: number } = {}) =>
    restClient
      .get(`${endpoints.gps.activeSessions}?${q({ page: p.page ?? 1, limit: p.limit ?? 20 })}`)
      .then(r => (r.data?.data ?? { page: 1, sessions: [], total: 0 }) as Paged<ActiveSession>),

  // Any one of the ids may be used to look a session up.
  search: (p: { booking_session_id?: number | string; booking_id?: number | string; trainer_id?: number | string; student_id?: number | string; page?: number; limit?: number }) =>
    restClient
      .get(`${endpoints.gps.search}?${q({ ...p, page: p.page ?? 1, limit: p.limit ?? 20 })}`)
      .then(r => (r.data?.data ?? { page: 1, sessions: [], total: 0 }) as Paged<SearchSession>),

  // Accepts booking_session_id or tracking_session_id.
  sessionDetail: (id: number | string) =>
    restClient.get(`${endpoints.gps.sessionDetail}${id}`).then(r => r.data?.data as SessionDetail),

  // Keyed by tracking_session_id.
  sessionRoute: (trackingId: number | string) =>
    restClient
      .get(endpoints.gps.sessionRoute(trackingId))
      .then(r => (r.data?.data?.points ?? []) as RoutePoint[]),
}
